class ProductInfo extends HTMLElement {
    constructor() {
        super();
        this.variant = null;

        this._boundOnVariantChange = this.onVariantChange.bind(this);
        this._boundOnQtyChange = this.onQtyChange.bind(this);
        this._boundOnQuantityEvent = this.updateEcoParticipation.bind(this);
    }

    connectedCallback () {
        this.productInfo = this.querySelector('.product-info__content');
        this.productImage = this.querySelector('.product-info__image');
        this.initialPosition = this.productInfo ? this.productInfo.offsetTop : 0;

        document.documentElement.addEventListener('variant:change', this._boundOnVariantChange);
        this.addEventListener('change', this._boundOnQtyChange);
        this.addEventListener('input', this._boundOnQtyChange);
        document.addEventListener('quantity:change', this._boundOnQuantityEvent);

        this.updateBindings();
        this.updateAttributeBindings();
        this.updateEcoParticipation();
    }

    disconnectedCallback () {
        document.documentElement.removeEventListener('variant:change', this._boundOnVariantChange);
        this.removeEventListener('change', this._boundOnQtyChange);
        this.removeEventListener('input', this._boundOnQtyChange);
        document.removeEventListener('quantity:change', this._boundOnQuantityEvent);
    }

    onVariantChange (event) {
        const { variant } = event.detail || {};
        this.variant = variant || null;
        this.updateBindings();
        this.updateAttributeBindings();
        this.updateEcoParticipation();
    }

    onQtyChange (event) {
        if (!event.target) return;

        const ecoBlock = this.querySelector('[data-ecoparticipation]');
        if (!ecoBlock) return;

        const selector = ecoBlock.getAttribute('data-eco-qty-selector') || 'input[name="quantity"]';
        if (event.target.matches(selector)) {
            this.updateEcoParticipation();
        }
    }

    resolvePath (path) {
        if (!path) return null;

        const keys = path.split('.');
        let value = this;

        keys.forEach((key) => {
            if (value) value = value[key];
        });

        return value;
    }

    updateBindings () {
        const bindings = this.querySelectorAll('[data-model]');

        bindings.forEach((el) => {
            const value = this.resolvePath(el.dataset.model);

            if (value !== undefined && value !== null) {
                el.textContent = value;
            }
        });
    }

    updateAttributeBindings () {
        const bindings = this.querySelectorAll('[data-model-attr]');

        bindings.forEach((el) => {
            const raw = el.dataset.modelAttr;
            if (!raw || !raw.includes(':')) return;

            const parts = raw.split(':');
            const attributeName = parts.shift()?.trim();
            const modelPath = parts.join(':').trim();
            if (!attributeName || !modelPath) return;

            const value = this.resolvePath(modelPath);
            if (value === undefined || value === null || value === '') return;

            el.setAttribute(attributeName, value);
        });
    }

    formatMoney (cents) {
        if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
            const fmt = window.theme?.moneyFormat || window.Shopify.money_format;
            const formatted = window.Shopify.formatMoney(cents, fmt);

            if (typeof formatted === 'string' && formatted.includes('€')) {
                return formatted.replace(/\s*€/, '').trim() + ' €';
            }

            return formatted;
        }

        return (cents / 100).toFixed(2).replace('.', ',') + ' €';
    }

    readQty (el) {
        const v = parseInt(el?.value || '0', 10);
        return Number.isFinite(v) && v > 0 ? v : 0;
    }

    normalizeEcoUnitCents (raw) {
        if (raw === null || raw === undefined || raw === '') return 0;

        const numeric = Number(raw);
        if (!Number.isFinite(numeric)) return 0;

        // Variant custom eco-participation is usually stored in euros.
        // If the value is already in cents (>= 100), keep it as-is.
        return numeric < 100 ? Math.round(numeric * 100) : Math.round(numeric);
    }

    getSelectedVariantIdFromInput (input) {
        const raw = input?.value || input?.getAttribute('value') || '';
        const parsed = parseInt(raw, 10);
        return Number.isFinite(parsed) ? parsed : null;
    }

    getVariantIdForQuantityInput (qtyInput) {
        const explicit = qtyInput.getAttribute('data-variant-id');
        if (explicit) {
            const parsed = parseInt(explicit, 10);
            if (Number.isFinite(parsed)) return parsed;
        }

        const row = qtyInput.closest('[data-variant-id]');
        if (row) {
            const parsed = parseInt(row.getAttribute('data-variant-id') || '', 10);
            if (Number.isFinite(parsed)) return parsed;
        }

        const form = qtyInput.closest('form');
        if (!form) return null;

        const variantInput = form.querySelector('input[name="id"], input[name="items[0][id]"], [data-main-variant-input]');
        return this.getSelectedVariantIdFromInput(variantInput);
    }

    getVariantEcoCentsById (variantId) {
        if (!variantId) return 0;

        if (this.variant && Number(this.variant.id) === Number(variantId)) {
            return this.normalizeEcoUnitCents(this.variant?.custom?.eco_participation);
        }

        const variantsScript = this.querySelector('[data-variants]');
        if (variantsScript) {
            try {
                const variants = JSON.parse(variantsScript.textContent || '[]');
                const found = variants.find((entry) => Number(entry.id) === Number(variantId));
                if (found) return this.normalizeEcoUnitCents(found?.custom?.eco_participation);
            } catch (error) {
                console.warn('[ProductInfo] Unable to parse [data-variants] JSON', error);
            }
        }

        const singleVariantScript = this.querySelector('[data-variant]');
        if (singleVariantScript) {
            try {
                const selectedVariant = JSON.parse(singleVariantScript.textContent || '{}');
                if (Number(selectedVariant.id) === Number(variantId)) {
                    return this.normalizeEcoUnitCents(selectedVariant?.custom?.eco_participation);
                }
            } catch (error) {
                console.warn('[ProductInfo] Unable to parse [data-variant] JSON', error);
            }
        }

        return 0;
    }

    getEcoLineItems (root, qtySelector) {
        const qtyInputs = Array.from(root.querySelectorAll(qtySelector));

        return qtyInputs.map((input) => {
            const quantity = this.readQty(input);
            const variantId = this.getVariantIdForQuantityInput(input);
            const ecoUnitCents = this.getVariantEcoCentsById(variantId);

            return {
                input,
                quantity,
                variantId,
                ecoUnitCents,
                totalCents: quantity * ecoUnitCents
            };
        }).filter((line) => line.quantity > 0 && line.ecoUnitCents > 0);
    }

    renderEcoParticipationAmount (amountEl, totalCents) {
        if (!amountEl) return;
        amountEl.textContent = this.formatMoney(totalCents);
    }

    updateEcoParticipation () {
        const blocks = this.querySelectorAll('[data-ecoparticipation]');
        if (!blocks.length) return;

        blocks.forEach((root) => {
            const qtySelector = root.getAttribute('data-eco-qty-selector') || 'input[name="quantity"]';
            const amountEl = root.querySelector('[data-ecoparticipation-amount]');
            const scope = root.closest('form') || root.closest('[data-product-form]') || this;

            const items = this.getEcoLineItems(scope, qtySelector);
            const totalCents = items.reduce((sum, item) => sum + item.totalCents, 0);

            root.hidden = totalCents <= 0;
            this.renderEcoParticipationAmount(amountEl, totalCents);
        });
    }
}

window.customElements.define('product-info', ProductInfo);